import { inject, Injectable } from '@angular/core';
import { UserGeolocConsentService } from '../../shared/providers/auth/user-geoloc-consent.service';
import { GeoLocalisationProvider } from '../../shared/providers/geolocalisation/geolocation.provider';
import { LoggerWrapper } from '../../shared/providers/logger/logger-wrapper.service';
import { OtWithStiProvider } from '../../shared/providers/ots-sti/ots-sti';
import { ActionEtapeSTI, StiProvider } from '../../shared/providers/sti/sti.provider';
import {
  ActiviteEnCours,
  ActiviteEnCoursService,
} from '../../shared/components/activite-en-cours/activite-en-cours.service';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { StiDto } from '../../api/models/sti-dto';
import { ActivitesProvider } from '../../shared/providers/activites/activites';
import { ChronoActiviteService } from '../../shared/providers/chrono-activite/chrono-activite.service';
import { Location } from '@capacitor-community/background-geolocation';
import { Router } from '@angular/router';
import { ChronoActivite, StatutActivite } from '../../shared/providers/chrono-activite/chrono-activite.model';
import { LoggerFactory } from '../../shared/providers/logger/logger-factory.service';
import { OtResponse } from '../../api/models/ot-response';
import { LoaderService } from '../../shared/providers/loading.service';
import { LocalisationsProvider } from '../../shared/providers/localisations/localisations';
import { InventaireLocalisationAdresseResponse } from '../../shared/models/inventaire.model';

@Injectable({
  providedIn: 'root',
})
export class DemarrerOtService {
  private readonly logger: LoggerWrapper = inject(LoggerFactory).getInstance(this, true);
  private activiteEnCoursService: ActiviteEnCoursService = inject(ActiviteEnCoursService);
  private userGeolocConsentService: UserGeolocConsentService = inject(UserGeolocConsentService);
  private geoLocalisationProvider: GeoLocalisationProvider = inject(GeoLocalisationProvider);
  private demandesStiProvider: OtWithStiProvider = inject(OtWithStiProvider);
  private stiProvider: StiProvider = inject(StiProvider);
  private alertCtrl: AlertController = inject(AlertController);
  private translate: TranslateService = inject(TranslateService);
  private activitesProvider: ActivitesProvider = inject(ActivitesProvider);
  private stiChronoProvider: StiProvider = inject(StiProvider);
  private chronoActiviteService: ChronoActiviteService = inject(ChronoActiviteService);
  private router: Router = inject(Router);
  private loaderService: LoaderService = inject(LoaderService);
  private localisationsProvider: LocalisationsProvider = inject(LocalisationsProvider);

  async startOrRestartOt(idOt: string): Promise<void> {
    const activiteEnCours: ActiviteEnCours = await this.activiteEnCoursService.getActiviteEnCours();
    const activiteEnPause: ActiviteEnCours = await this.activiteEnCoursService.getActiviteEnPause(idOt);

    if (activiteEnCours) {
      if (activiteEnCours.id === idOt) {
        this.logger.debug(`OT ${idOt} déjà démarré`);
      } else if (activiteEnCours.type === 'ot') {
        await this.showAlertOtEnCours(idOt, activiteEnCours.id);
      } else {
        this.alertCtrl
          .create({
            message: this.translate.instant('GLOBAL.TOO_MANY_ACTIVITES'),
            buttons: [this.translate.instant('GLOBAL.OK')],
          })
          .then((alert) => alert.present());
      }
    } else if (activiteEnPause) {
      await this.reprendreOtEnPause(activiteEnPause);
    } else {
      await this.demarrerOt(idOt);
    }
  }

  private async demarrerOt(idOt: string): Promise<void> {
    if (!(await this.geoLocalisationProvider.hasGeolocAndBatteryPermissions())) {
      return;
    }

    try {
      await this.loaderService.present(this.translate.instant('CHRONO.INTERVENTION_DEMARRAGE'));
      this.logger.debug(`Démarrage de l'OT ${idOt}`);
      const otEncours: ActiviteEnCours = await this.createOtEnCours(idOt);
      if (otEncours !== null) {
        await this.activiteEnCoursService.demarrerActiviteEncours(otEncours);
        await this.chronoActiviteService.demarrerChronoPourActivite(idOt, otEncours.type);
        await this.demarrerGeoloc(otEncours);
        await this.activerSti(otEncours);
      }
    } finally {
      await this.loaderService.dismiss();
    }
  }

  private async createOtEnCours(id_ot: string): Promise<ActiviteEnCours> {
    let activiteEnCours: ActiviteEnCours = null;
    try {
      const otReponse: OtResponse = await this.activitesProvider.getOt(id_ot);
      let stis: StiDto[] = await this.stiChronoProvider.getSTIs(id_ot);

      let siteLocations: Location[] = [];

      let localisations = await this.localisationsProvider.getLocalisationsBySite(otReponse.site.id_localisation);
      let siteAddressList: InventaireLocalisationAdresseResponse[] =
        localisations?.flatMap((loc) => loc.adresses ?? []) ?? [];

      if (otReponse.localisation?.adresse_intervention) {
        siteAddressList.push(otReponse.localisation.adresse_intervention);
      }
      if (otReponse.site?.adresse_intervention) {
        siteAddressList.push(otReponse.site.adresse_intervention);
      }
      if (otReponse.localisation?.adresse_principale) {
        siteAddressList.push(otReponse.localisation.adresse_principale);
      }
      if (otReponse.site?.adresse_principale) {
        siteAddressList.push(otReponse.site.adresse_principale);
      }

      const parseGpsCoordinate = (coord: any): number | null => {
        if (coord == null) return null;
        const parsed = parseFloat(String(coord).replace(',', '.'));
        return isNaN(parsed) ? null : parsed;
      };

      siteAddressList.forEach((address) => {
        const lat = parseGpsCoordinate(address.gps_y);
        const lng = parseGpsCoordinate(address.gps_x);
        if (lat !== null && lng !== null) {
          siteLocations.push({
            latitude: lat,
            longitude: lng,
            accuracy: 0,
            altitude: 0,
            altitudeAccuracy: 0,
            bearing: 0,
            simulated: false,
            speed: 0,
            time: 0,
          });
        }
      });

      activiteEnCours = {
        id: id_ot,
        type: 'ot',
        type_ot: otReponse.type_activite,
        libelle: otReponse.libelle_intervention,
        site_id: otReponse.site.id_localisation,
        site_name: otReponse.site.libelle_long,
        id_sti: stis ? stis[0]?.id : undefined,
        siteLocations: siteLocations,
      };
    } catch (e) {
      this.logger.error(e, "Erreur lors de la création de l'OT en cours");
    }
    return activiteEnCours;
  }

  private async activerSti(otEncours: ActiviteEnCours) {
    if (otEncours.id_sti) {
      this.logger.debug(`Activation du sti ${otEncours.id_sti} pour l'OT ${otEncours.id}`);
      await this.demandesStiProvider.activateSti(otEncours.id);
    }
  }

  private async demarrerGeoloc(otEncours: ActiviteEnCours) {
    if (otEncours === null) {
      return;
    }
    if (otEncours.id_sti) {
      this.logger.debug(`Démarrage de la geolocalisation pour l'OT ${otEncours.id} et le sti ${otEncours.id_sti}`);
      await this.geoLocalisationProvider.startGeolocation(
        otEncours.id,
        true,
        'STI en cours',
        'Votre position est en cours de suivi pour le STI.'
      );
    } else if (this.userGeolocConsentService.getCurrentConsentChrono()) {
      this.logger.debug(`Démarrage de la geolocalisation pour l'OT ${otEncours.id}`);
      await this.geoLocalisationProvider.startGeolocation(
        otEncours.id,
        false,
        'OT en cours',
        'Votre position est en cours de suivi pour votre OT.'
      );
    }
  }

  async stopOT(idOt: string) {
    this.logger.debug(`Stop de l'OT ${idOt}`);
    const otEncours = await this.activiteEnCoursService.getActiviteEnCours();
    if (otEncours?.id === idOt) {
      await this.chronoActiviteService.supprimerChronoPourActivite(idOt);
      await this.geoLocalisationProvider.stopGeolocation(otEncours.id);
      await this.stopperSti(otEncours);
      await this.activiteEnCoursService.supprimerActiviteEnCours(idOt);
      await this.activiteEnCoursService.supprimerActiviteEnPause(idOt);
    }
  }

  private async stopperSti(otEncours: ActiviteEnCours) {
    if (otEncours?.id_sti) {
      this.logger.debug(`Activation du sti ${otEncours.id_sti} pour l'OT ${otEncours.id}`);
      await this.stiProvider.actionSTI(otEncours.id_sti, ActionEtapeSTI.CLOTURE_ETAPE);
    }
  }

  private async reprendreOtEnPause(activiteEnPause: ActiviteEnCours) {
    try {
      await this.loaderService.present(this.translate.instant('CHRONO.INTERVENTION_REPRISE'));

      let chrono: ChronoActivite = await this.chronoActiviteService.getChronoActivite(activiteEnPause.id);
      this.logger.debug(`Reprise de l'OT en pause ${activiteEnPause.id}`, chrono);
      if (chrono?.horodatageEncours?.statutActivite === StatutActivite.pause) {
        await this.activiteEnCoursService.reprendreActiviteEnPause(activiteEnPause.id);
        await this.chronoActiviteService.reprendreChronoEnPausePourActivite(activiteEnPause.id);
        await this.demarrerGeoloc(activiteEnPause);
        await this.activerSti(activiteEnPause);
      } else {
        this.logger.debug(`Ot dèjà démarré ${activiteEnPause.id}`);
      }
    } finally {
      await this.loaderService.dismiss();
    }
  }

  private async showAlertOtEnCours(idNouveauOt: string, idOtEnCours: string) {
    let alert = await this.alertCtrl.create({
      message: this.translate.instant('GLOBAL.TOO_MANY_OTS'),
      buttons: [
        {
          text: 'Saisir un CR partiel',
          handler: async () => {
            await this.router.navigate(['/mnt/ots/', idOtEnCours]);
          },
        },
        {
          text: 'Mettre l’OT en pause',
          handler: async () => {
            await this.mettreAnPauseOt(idOtEnCours);
            const activiteEnPause: ActiviteEnCours = await this.activiteEnCoursService.getActiviteEnPause(idNouveauOt);
            if (activiteEnPause) {
              await this.reprendreOtEnPause(activiteEnPause);
            } else {
              await this.demarrerOt(idNouveauOt);
            }
          },
        },
      ],
    });
    await alert.present();
  }

  async mettreAnPauseOt(idOt: string) {
    await this.geoLocalisationProvider.stopGeolocation(idOt);
    await this.activiteEnCoursService.mettreActiviteEnPause(idOt);
    await this.chronoActiviteService.mettreChronoEnPausePourActivite(idOt);
  }

  async reloadOt() {
    const activiteEnCours: ActiviteEnCours = await this.activiteEnCoursService.reloadActivite();
    if (activiteEnCours) {
      await this.chronoActiviteService.reloadChrono(activiteEnCours.id);
      await this.demarrerGeoloc(activiteEnCours);
    }
  }
}
